import { useState } from "react";
import { Crown } from "lucide-react";
import { toast } from "sonner";
import { Button, Card, CardContent, CardHeader, CardTitle, Label } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { DialogoDeAcao } from "./DialogoDeAcao";

type Vinculo = { role: string; organization: { id: string; name: string; isPersonal: boolean } };

/** Organizações de equipe de que a pessoa é dona — o admin passa a propriedade pra outro membro
 *  quando ela não consegue (sumiu, perdeu acesso, vai excluir a conta). */
export function TransferirPropriedade({ userId, vinculos }: { userId: string; vinculos: Vinculo[] }) {
  const utils = trpc.useUtils();
  const donas = vinculos.filter((v) => v.role === "OWNER" && !v.organization.isPersonal);
  const [org, setOrg] = useState<{ id: string; name: string } | null>(null);
  const [escolhido, setEscolhido] = useState("");
  const membros = trpc.admin.conta.membrosDaOrganizacao.useQuery({ organizationId: org?.id ?? "" }, { enabled: !!org });
  const transferir = trpc.admin.conta.transferirPropriedade.useMutation({
    onSuccess: () => {
      toast.success("Propriedade transferida.");
      utils.admin.users.getById.invalidate({ id: userId });
      utils.admin.auditoria.list.invalidate();
      setOrg(null);
      setEscolhido("");
    },
    onError: (e) => toast.error(e.message),
  });
  if (donas.length === 0) return null;
  const candidatos = (membros.data ?? []).filter((m) => m.user.id !== userId);

  return (
    <Card className="border-admin-200">
      <CardHeader>
        <CardTitle>Propriedade</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {donas.map((v) => (
          <div key={v.organization.id} className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5 text-admin-900"><Crown className="h-3.5 w-3.5 text-amber-600" />{v.organization.name}</span>
            <Button size="sm" variant="outline" onClick={() => setOrg(v.organization)}>Transferir</Button>
          </div>
        ))}
      </CardContent>

      <DialogoDeAcao
        aberto={!!org}
        onFechar={() => { setOrg(null); setEscolhido(""); }}
        titulo={`Transferir ${org?.name ?? ""}`}
        descricao="O membro escolhido vira dono; a dona atual passa a Administradora. A equipe é avisada."
        rotulo="Transferir"
        perigosa
        pronto={!!escolhido}
        ocupado={transferir.isPending}
        onConfirmar={() => org && transferir.mutate({ organizationId: org.id, novoDonoMembershipId: escolhido })}
      >
        <Label htmlFor="novo-dono">Novo dono</Label>
        <select id="novo-dono" value={escolhido} onChange={(e) => setEscolhido(e.target.value)} className="h-9 w-full rounded-md border border-zinc-200 bg-white px-2 text-sm">
          <option value="">{membros.isLoading ? "Carregando…" : candidatos.length ? "Selecione" : "Nenhum outro membro ativo"}</option>
          {candidatos.map((m) => (
            <option key={m.id} value={m.id}>{m.user.name ?? m.user.email} — {m.user.email}</option>
          ))}
        </select>
      </DialogoDeAcao>
    </Card>
  );
}
